import { ContentCompetency, mapCareerPathResponseToCareerPath } from '@backrs_ui/common-ui';

import { CareerQuestion } from '../models/models';
import {
  CareerPathContentResponse,
  fetchCareerPathsAndRoles,
} from '../redux/careerPath/thunks/careerPath.thunk';
import { fetchContentCategories } from '../redux/contentCategory/contentCategory';
import { fetchContentCompetencies } from '../redux/contentCompetency/contentCompetency';
import { fetchDataDictionary } from '../redux/dataDictionary/thunks/dataDictionary.thunk';
import { fetchFeatureFlags } from '../redux/featureFlags/featureFlags';
import { AppDispatch } from '../redux/store';
import { fetchCareerQuestions } from '../redux/survey/thunks/survey.thunk';
import { fetchUserViaAutoLogin } from '../redux/user/thunks/user.thunk';
import { checkUserDocuments } from '../redux/user/thunks/userDocument.thunk';
import { userApi } from '../redux/user/user.api';
import { getMostRecentUserResponseSetFromAPI } from '../redux/userResponse/thunks/userResponse.thunk';

export const fetchFeatureFlagsData = async (dispatch: AppDispatch) => {
  try {
    const resultAction = await dispatch(fetchFeatureFlags());

    if (fetchFeatureFlags.rejected.match(resultAction)) {
      console.error('Failed to fetch feature flags:', resultAction.payload);
    }
  } catch (error) {
    console.error('Failed to fetch feature flags:', error);
  }
};

export const fetchQuestions = async (dispatch: AppDispatch): Promise<CareerQuestion[]> => {
  try {
    const resultAction = await dispatch(fetchCareerQuestions());

    if (fetchCareerQuestions.rejected.match(resultAction)) {
      throw new Error(resultAction.payload as string);
    }

    return resultAction.payload as CareerQuestion[];
  } catch (error) {
    console.error('Failed to fetch career questions', error);
    throw error;
  }
};

export const fetchAllCareerPathContent = async (
  dispatch: AppDispatch,
): Promise<CareerPathContentResponse> => {
  try {
    const resultAction = await dispatch(fetchCareerPathsAndRoles());

    if (fetchCareerPathsAndRoles.rejected.match(resultAction)) {
      throw new Error(resultAction.payload as string);
    }

    return resultAction.payload;
  } catch (error) {
    console.error('Failed to fetch career paths', error);
    throw error;
  }
};

export const fetchAuthenticatedUser = async (dispatch: AppDispatch) => {
  try {
    // Permissions only needs the stored token, so it runs concurrently with auto-login.
    const [resultAction] = await Promise.all([
      dispatch(fetchUserViaAutoLogin()),
      dispatch(userApi.endpoints.fetchUserPermissions.initiate(undefined)),
    ]);

    if (fetchUserViaAutoLogin.rejected.match(resultAction)) {
      throw new Error(resultAction.payload as string);
    }

    return resultAction.payload;
  } catch (error) {
    console.error('Failed to fetch user', error);
    throw error;
  }
};

export const populateDataDictionary = async (
  dispatch: AppDispatch,
  competencies: ContentCompetency[],
) => {
  try {
    const resultAction = await dispatch(fetchDataDictionary(competencies));

    if (fetchDataDictionary.rejected.match(resultAction)) {
      console.error(resultAction.payload as string);
    }
  } catch (error) {
    console.error('Failed to populate data dictionary', error);
    throw error;
  }
};

export const handleFetchingAuthenticatedContent = async (dispatch: AppDispatch) => {
  const questions = await fetchQuestions(dispatch);
  const careerPathContent = await fetchAllCareerPathContent(dispatch);

  await fetchProsperContent(dispatch);
  await fetchUserInfo({ dispatch, questions, careerPathContent });
};

export const fetchProsperContent = async (dispatch: AppDispatch) => {
  await fetchCategories(dispatch);
};

type FetchUserInfoParams = Readonly<{
  dispatch: AppDispatch;
  questions: CareerQuestion[];
  careerPathContent: CareerPathContentResponse;
}>;

export const fetchUserInfo = async ({
  dispatch,
  questions,
  careerPathContent,
}: FetchUserInfoParams) => {
  try {
    await dispatch(checkUserDocuments());
    await fetchMostRecentUserResponseSet(dispatch, questions, careerPathContent);
  } catch (error) {
    console.error('Failed to fetch user info:', error);
  }
};

export const fetchMostRecentUserResponseSet = async (
  dispatch: AppDispatch,
  questions?: CareerQuestion[],
  careerPathContentResponse?: CareerPathContentResponse,
) => {
  if (!questions || !careerPathContentResponse) {
    console.error(
      'Questions and career path content must be provided to fetch most recent user response set',
    );

    return;
  }

  const { careerPaths, careerPathRoles, careerPathMeanings, careerPathEnjoyments } =
    careerPathContentResponse;

  const mappedCareerPaths = careerPaths.map(mapCareerPathResponseToCareerPath);

  try {
    const resultAction = await dispatch(
      getMostRecentUserResponseSetFromAPI({
        careerQuestions: questions,
        careerPaths: mappedCareerPaths,
        careerPathRoles: careerPathRoles,
        careerPathMeanings: careerPathMeanings,
        careerPathEnjoyments: careerPathEnjoyments,
      }),
    );

    if (getMostRecentUserResponseSetFromAPI.rejected.match(resultAction)) {
      console.error(resultAction.payload as string);
    }
  } catch (error) {
    console.error('Failed to fetch most recent user response set', error);
    throw error;
  }
};

const fetchCategories = async (dispatch: AppDispatch) => {
  try {
    await dispatch(fetchContentCategories());
  } catch (error) {
    console.error('Failed to fetch content categories:', error);
  }
};

export const fetchCompetencies = async (
  dispatch: AppDispatch,
): Promise<ContentCompetency[] | undefined> => {
  try {
    const resultAction = await dispatch(fetchContentCompetencies());

    if (fetchContentCompetencies.rejected.match(resultAction)) {
      console.error(resultAction.payload as string);
    }

    return resultAction.payload as ContentCompetency[];
  } catch (error) {
    console.error('Failed to fetch content competencies:', error);
  }
};
